import { useEffect, useState } from 'react';
import type { CognitoUser } from 'amazon-cognito-identity-js';
import { QRCodeSVG } from 'qrcode.react';
import {
  signIn,
  signUp,
  confirmSignUp,
  resendConfirmationCode,
  completeNewPassword,
  startMfaSetup,
  verifyMfaSetup,
  submitMfaCode,
  signInWithBackupCode,
  forgotPassword,
  confirmForgotPassword,
} from '../lib/auth';
import { acceptInvite, fetchInvite } from '../lib/api';
import type { InviteSummary } from '../types/moderation';
import BackupCodes from './BackupCodes';
import {
  isPasskeySupported,
  signInWithPasskey,
  listPasskeys,
  registerPasskey,
} from '../lib/webauthn';

interface LoginScreenProps {
  onAuthenticated: () => void;
  /**
   * Optional banner message shown above the form. Used by the idle-timeout
   * flow ("You were signed out due to inactivity.") and the absolute-cap
   * flow ("Your session expired.").
   */
  banner?: string;
}

type Mode =
  | { kind: 'signin' }
  | { kind: 'signup' }
  | { kind: 'signup-confirm'; email: string }
  | { kind: 'new-password'; user: CognitoUser; userAttributes: Record<string, unknown> }
  | { kind: 'mfa-setup'; user: CognitoUser; secret: string; uri: string; account: string }
  | { kind: 'mfa-code'; user: CognitoUser }
  | { kind: 'backup-code'; email: string }
  | { kind: 'backup-codes' }
  | { kind: 'passkey-nudge' }
  | { kind: 'forgot' }
  | { kind: 'forgot-confirm'; email: string };

/** Read the invite token from the URL hash (`#/signup?token=abc`). */
function parseInviteTokenFromHash(): string | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash || '';
  const queryIdx = hash.indexOf('?');
  if (queryIdx === -1) return null;
  const params = new URLSearchParams(hash.slice(queryIdx + 1));
  return params.get('token');
}

/**
 * F-112: remember the last successfully-used email so the LoginScreen
 * can pre-fill it after an idle sign-out. Stored under a versioned key
 * so the existing localStorage shape isn't affected.
 */
const LAST_EMAIL_KEY = 'moderation-ui:last-email';

function readLastEmail(): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(LAST_EMAIL_KEY) || '';
  } catch {
    return '';
  }
}

export function rememberLastEmail(email: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LAST_EMAIL_KEY, email);
  } catch {
    // ignore quota / privacy errors
  }
}

export default function LoginScreen({ onAuthenticated, banner }: LoginScreenProps) {
  const [mode, setMode] = useState<Mode>(() => {
    const token = parseInviteTokenFromHash();
    return token ? { kind: 'signup' } : { kind: 'signin' };
  });
  const [email, setEmail] = useState<string>(() => readLastEmail());
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [inviteToken] = useState<string | null>(() => parseInviteTokenFromHash());
  const [invite, setInvite] = useState<InviteSummary | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  /**
   * F-310: "Trust this device" checkbox surfaced on the MFA prompt.
   * The UI captures the preference; Cognito's remembered-device flow
   * still needs a server-side `setUserMFAPreference` plus
   * `confirmDevice` call to take full effect, but the moderation UI
   * already passes the choice through so the Cognito wrapper can
   * complete it when wired up.
   */
  const [trustDevice, setTrustDevice] = useState(false);

  function clearStatus() {
    setError(null);
    setInfo(null);
  }

  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    fetchInvite(inviteToken)
      .then((data) => {
        if (cancelled) return;
        setInvite(data);
        setEmail(data.email);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setInviteError(
          err.message || 'This invite link is invalid or has expired.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    clearStatus();
    try {
      const result = await signIn(email, password);
      await advance(result);
    } catch (err) {
      setError((err as Error).message || 'Sign in failed');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Phase 7 passkey sign-in. Email is the only required field; on
   * success the WebAuthn helper bridges Cognito tokens into the legacy
   * SDK session so `onAuthenticated` can move us into the app.
   * On miss or cancel we silently leave the form alone so the
   * moderator can fall back to email + password.
   */
  async function handlePasskeySignIn() {
    if (!email) {
      setError('Enter your email above, then sign in with your passkey.');
      return;
    }
    setBusy(true);
    clearStatus();
    try {
      const result = await signInWithPasskey(email);
      if (result.ok) {
        rememberLastEmail(email);
        onAuthenticated();
        return;
      }
      if (result.reason === 'no-passkey') {
        setInfo(
          'No passkey was found for this account. Sign in with your password below, then add a passkey from Settings -> Security.',
        );
      } else if (result.reason === 'cancelled') {
        // Silent: the user changed their mind in the OS picker.
      } else {
        setError(
          result.error?.message || 'Passkey sign-in failed. Try your password.',
        );
      }
    } finally {
      setBusy(false);
    }
  }

  /**
   * Final step after a password / MFA / backup-code sign-in. If the
   * device supports passkeys and the account has none registered yet,
   * surface the (skippable) enrolment nudge before handing off to the
   * app. Anything unexpected fails open: we never block a valid login
   * on the passkey check.
   *
   * The passkey sign-in path does NOT call this (those users already
   * have a passkey) and goes straight to `onAuthenticated`.
   */
  async function finishAuth() {
    if (email) rememberLastEmail(email);
    if (!isPasskeySupported()) {
      onAuthenticated();
      return;
    }
    try {
      const existing = await listPasskeys();
      if (existing.length === 0) {
        clearStatus();
        setMode({ kind: 'passkey-nudge' });
        return;
      }
    } catch {
      // Listing failed (offline, SDK shape, etc.) — don't gate login on it.
    }
    onAuthenticated();
  }

  /** Enrol a passkey from the post-sign-in nudge, then enter the app. */
  async function handleAddPasskey() {
    setBusy(true);
    clearStatus();
    try {
      await registerPasskey();
      onAuthenticated();
    } catch (err) {
      setError(
        (err as Error).message ||
          'Could not add a passkey. You can add one later from Settings -> Security.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (
      invite &&
      email.toLowerCase().trim() !== invite.email.toLowerCase()
    ) {
      setError(`This invite is for ${invite.email}.`);
      return;
    }
    setBusy(true);
    clearStatus();
    try {
      await signUp(email, password, inviteToken || undefined);
      setMode({ kind: 'signup-confirm', email });
      setInfo('We emailed you a verification code. Enter it below to finish creating your account.');
      setCode('');
    } catch (err) {
      setError((err as Error).message || 'Sign up failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleSignupConfirm(e: React.FormEvent) {
    e.preventDefault();
    if (mode.kind !== 'signup-confirm') return;
    setBusy(true);
    clearStatus();
    try {
      await confirmSignUp(mode.email, code);
      if (inviteToken) {
        try {
          await acceptInvite(inviteToken, mode.email);
        } catch (err) {
          console.error('accept-invite failed', err);
        }
      }
      setInfo('Your account is verified. Sign in below. The first time you sign in we will set up an authenticator app.');
      setMode({ kind: 'signin' });
      setCode('');
      setPassword('');
    } catch (err) {
      setError((err as Error).message || 'Verification failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleResendCode() {
    if (mode.kind !== 'signup-confirm') return;
    setBusy(true);
    clearStatus();
    try {
      await resendConfirmationCode(mode.email);
      setInfo('We sent a fresh code. Check your email.');
    } catch (err) {
      setError((err as Error).message || 'Could not resend code');
    } finally {
      setBusy(false);
    }
  }

  async function handleNewPassword(e: React.FormEvent) {
    e.preventDefault();
    if (mode.kind !== 'new-password') return;
    setBusy(true);
    clearStatus();
    try {
      const result = await completeNewPassword(mode.user, newPassword, mode.userAttributes);
      await advance(result);
    } catch (err) {
      setError((err as Error).message || 'Failed to set password');
    } finally {
      setBusy(false);
    }
  }

  async function handleVerifyMfaSetup(e: React.FormEvent) {
    e.preventDefault();
    if (mode.kind !== 'mfa-setup') return;
    setBusy(true);
    clearStatus();
    try {
      await verifyMfaSetup(mode.user, code);
      // After first TOTP setup we surface backup codes before letting the
      // moderator land in the workbench.
      setMode({ kind: 'backup-codes' });
    } catch (err) {
      setError((err as Error).message || 'Code did not match. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function handleMfaCode(e: React.FormEvent) {
    e.preventDefault();
    if (mode.kind !== 'mfa-code') return;
    setBusy(true);
    clearStatus();
    try {
      // F-310: pass the "trust this device" preference through to
      // the Cognito helper.
      await submitMfaCode(mode.user, code, trustDevice);
      await finishAuth();
    } catch (err) {
      setError((err as Error).message || 'Code did not match. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function handleBackupCode(e: React.FormEvent) {
    e.preventDefault();
    if (mode.kind !== 'backup-code') return;
    setBusy(true);
    clearStatus();
    try {
      await signInWithBackupCode(mode.email, code.trim());
      await finishAuth();
    } catch (err) {
      setError(
        (err as Error).message ||
          'That backup code did not work. Try another, or use your authenticator app.',
      );
    } finally {
      setBusy(false);
    }
  }

  function useBackupCodeInstead() {
    // Carry the email from the in-progress mfa-code screen so the
    // backup-code flow does not have to re-prompt for it.
    setMode({ kind: 'backup-code', email });
    setCode('');
    clearStatus();
  }

  function backToTotp() {
    // From the backup-code screen, send the user back to the sign-in
    // form so they can re-authenticate with password + TOTP. We can't
    // jump straight to the mfa-code mode because the CognitoUser
    // session built for the custom-auth flow isn't a 1:1 swap for the
    // SRP one.
    setMode({ kind: 'signin' });
    setCode('');
    setPassword('');
    clearStatus();
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    if (mode.kind !== 'forgot') return;
    setBusy(true);
    clearStatus();
    try {
      await forgotPassword(email);
      setMode({ kind: 'forgot-confirm', email });
      setInfo('We emailed you a verification code. Enter it below with your new password.');
      setCode('');
      setNewPassword('');
    } catch (err) {
      setError((err as Error).message || 'Could not start password reset');
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirmForgot(e: React.FormEvent) {
    e.preventDefault();
    if (mode.kind !== 'forgot-confirm') return;
    setBusy(true);
    clearStatus();
    try {
      await confirmForgotPassword(mode.email, code, newPassword);
      setInfo('Your password has been reset. Sign in with your new password.');
      setMode({ kind: 'signin' });
      setCode('');
      setNewPassword('');
      setPassword('');
    } catch (err) {
      setError((err as Error).message || 'Could not reset password');
    } finally {
      setBusy(false);
    }
  }

  /** Advance the state machine based on a fresh sign-in / new-password result. */
  async function advance(result: Awaited<ReturnType<typeof signIn>>) {
    if (result.kind === 'authenticated') {
      // F-112: remember the signed-in email so subsequent idle-timeout
      // returns to LoginScreen can pre-fill it. finishAuth handles the
      // remember + the optional passkey-enrolment nudge.
      await finishAuth();
      return;
    }
    if (result.kind === 'newPasswordRequired') {
      setMode({ kind: 'new-password', user: result.user, userAttributes: result.userAttributes });
      setNewPassword('');
      return;
    }
    if (result.kind === 'mfaCodeRequired') {
      setMode({ kind: 'mfa-code', user: result.user });
      setCode('');
      return;
    }
    if (result.kind === 'mfaSetupRequired') {
      const setup = await startMfaSetup(result.user);
      setMode({
        kind: 'mfa-setup',
        user: result.user,
        secret: setup.secret,
        uri: setup.uri,
        account: setup.account,
      });
      setCode('');
    }
  }

  function switchTab(next: 'signin' | 'signup') {
    setMode({ kind: next });
    setError(null);
    setInfo(null);
    setPassword('');
    setConfirmPassword('');
    setCode('');
  }

  function startForgot() {
    setMode({ kind: 'forgot' });
    setError(null);
    setInfo(null);
    setPassword('');
  }

  return (
    <div className="login-screen">
      {/* F-007: skip link for keyboard users (also visible on the
          login surface for consistency). */}
      <a href="#login-card" className="skip-link" data-testid="skip-link">
        Skip to main content
      </a>
      <div className="login-card" id="login-card" tabIndex={-1}>
        <h1>Moderation Sign In</h1>
        <p className="login-subtitle">Brisbane Pride History Archive</p>

        {banner && (
          <div className="field-info" role="status" data-testid="login-banner">
            {banner}
            {email && (
              // F-112: surface the actionable hint -- the email is
              // already pre-filled and the password input is focused.
              <span className="login-banner__hint">
                {' '}Enter your password to continue as <strong>{email}</strong>.
              </span>
            )}
          </div>
        )}

        {(mode.kind === 'signin' || mode.kind === 'signup') && (
          <div className="login-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mode.kind === 'signin'}
              className={`login-tab ${mode.kind === 'signin' ? 'is-active' : ''}`}
              onClick={() => switchTab('signin')}
              data-testid="tab-signin"
            >
              Sign in
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode.kind === 'signup'}
              className={`login-tab ${mode.kind === 'signup' ? 'is-active' : ''}`}
              onClick={() => switchTab('signup')}
              data-testid="tab-signup"
            >
              Sign up
            </button>
          </div>
        )}

        {info && <div className="field-info" role="status" data-testid="login-info">{info}</div>}
        {error && <div className="field-error" role="alert" data-testid="login-error">{error}</div>}

        {mode.kind === 'signin' && (
          <form onSubmit={handleSignIn} data-testid="login-form">
            <label className="form-group">
              <span>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="login-email"
                /* F-112: don't autofocus the email field if it's
                   already pre-filled from the last sign-in. */
                autoFocus={!email}
              />
            </label>
            {/* Passkey-preferred: lead with the phishing-resistant option
                and demote the password form to a labelled fallback below. */}
            {isPasskeySupported() && (
              <>
                <button
                  type="button"
                  className="btn btn-primary login-passkey-btn"
                  onClick={handlePasskeySignIn}
                  disabled={busy}
                  data-testid="login-passkey"
                >
                  {busy ? 'Waiting for your device…' : 'Sign in with a passkey'}
                </button>
                <div className="login-divider" aria-hidden="true">
                  <span>or use your password</span>
                </div>
              </>
            )}
            <label className="form-group">
              <span>Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="login-password"
                /* F-112: focus the password field when email is
                   pre-filled (idle-timeout return path). */
                autoFocus={Boolean(email) && Boolean(banner)}
              />
            </label>
            <button
              type="submit"
              className={`btn ${isPasskeySupported() ? 'btn-secondary' : 'btn-primary'}`}
              disabled={busy}
              data-testid="login-submit"
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
            <button
              type="button"
              className="btn btn-link"
              onClick={startForgot}
              disabled={busy}
              data-testid="forgot-password-link"
            >
              Forgot password?
            </button>
          </form>
        )}

        {mode.kind === 'signup' && (
          <form onSubmit={handleSignUp} data-testid="signup-form">
            {invite ? (
              <p className="login-helper" data-testid="invite-info">
                You have been invited to join as a{' '}
                <strong>{invite.role.replace('_', ' ')}</strong> using{' '}
                <strong>{invite.email}</strong>.
              </p>
            ) : inviteToken ? (
              <p className="login-helper" data-testid="invite-error">
                {inviteError || 'Checking invite…'}
              </p>
            ) : (
              <p className="login-helper">
                Sign-up is restricted to <strong>@brisbanepride.org.au</strong> accounts.
              </p>
            )}
            <label className="form-group">
              <span>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="signup-email"
              />
            </label>
            <label className="form-group">
              <span>Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={12}
                data-testid="signup-password"
              />
            </label>
            <label className="form-group">
              <span>Confirm password</span>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={12}
                data-testid="signup-confirm-password"
              />
            </label>
            <p className="login-helper">
              Minimum 12 characters with upper, lower, digit, and symbol.
            </p>
            <button type="submit" className="btn btn-primary" disabled={busy} data-testid="signup-submit">
              {busy ? 'Creating account…' : 'Create account'}
            </button>
          </form>
        )}

        {mode.kind === 'signup-confirm' && (
          <form onSubmit={handleSignupConfirm} data-testid="signup-confirm-form">
            <p className="login-helper">
              Code sent to <strong>{mode.email}</strong>.
            </p>
            <label className="form-group">
              <span>Verification code</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoComplete="one-time-code"
                data-testid="signup-confirm-code"
              />
            </label>
            <button type="submit" className="btn btn-primary" disabled={busy} data-testid="signup-confirm-submit">
              {busy ? 'Verifying…' : 'Verify and continue'}
            </button>
            <button
              type="button"
              className="btn btn-link"
              onClick={handleResendCode}
              disabled={busy}
              data-testid="signup-confirm-resend"
            >
              Resend code
            </button>
          </form>
        )}

        {mode.kind === 'new-password' && (
          <form onSubmit={handleNewPassword} data-testid="new-password-form">
            <p>Please set a new password to continue.</p>
            <label className="form-group">
              <span>New password</span>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={12}
                data-testid="login-new-password"
              />
            </label>
            <button type="submit" className="btn btn-primary" disabled={busy} data-testid="new-password-submit">
              {busy ? 'Saving…' : 'Set password'}
            </button>
          </form>
        )}

        {mode.kind === 'mfa-setup' && (
          <form onSubmit={handleVerifyMfaSetup} data-testid="mfa-setup-form">
            <h2>Set up two-factor authentication</h2>
            <p className="login-helper">
              Scan this QR code with an authenticator app (1Password, Authy, Google Authenticator, etc.),
              then enter the 6-digit code it shows.
            </p>
            <div className="mfa-qr" data-testid="mfa-qr">
              <QRCodeSVG value={mode.uri} size={200} level="M" includeMargin />
            </div>
            <details className="mfa-secret">
              <summary>Can't scan? Enter the secret manually</summary>
              <code data-testid="mfa-secret">{mode.secret}</code>
            </details>
            <label className="form-group">
              <span>6-digit code</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoComplete="one-time-code"
                data-testid="mfa-setup-code"
              />
            </label>
            <button type="submit" className="btn btn-primary" disabled={busy} data-testid="mfa-setup-submit">
              {busy ? 'Verifying…' : 'Verify and continue'}
            </button>
          </form>
        )}

        {mode.kind === 'mfa-code' && (
          <form onSubmit={handleMfaCode} data-testid="mfa-code-form">
            <p className="login-helper">Enter the 6-digit code from your authenticator app.</p>
            <label className="form-group">
              <span>6-digit code</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoComplete="one-time-code"
                autoFocus
                data-testid="mfa-code-input"
              />
            </label>
            <label className="form-group form-group--inline">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                data-testid="trust-device"
              />
              <span>
                Trust this device for 30 days
                <span className="login-helper">
                  {' '}— skips MFA on subsequent logins from this browser.
                </span>
              </span>
            </label>
            <button type="submit" className="btn btn-primary" disabled={busy} data-testid="mfa-code-submit">
              {busy ? 'Verifying…' : 'Verify'}
            </button>
            <button
              type="button"
              className="btn btn-link"
              onClick={useBackupCodeInstead}
              disabled={busy}
              data-testid="use-backup-code-link"
            >
              Use a backup code instead
            </button>
            <button
              type="button"
              className="btn btn-link"
              onClick={startForgot}
              disabled={busy}
              data-testid="forgot-password-link-mfa"
            >
              Forgot password?
            </button>
          </form>
        )}

        {mode.kind === 'backup-code' && (
          <form onSubmit={handleBackupCode} data-testid="backup-code-form">
            <p className="login-helper">
              Enter one of the backup codes you saved when you set up two-factor
              authentication. Each code can only be used once.
            </p>
            <label className="form-group">
              <span>Backup code</span>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                maxLength={16}
                autoComplete="one-time-code"
                autoFocus
                autoCapitalize="characters"
                spellCheck={false}
                data-testid="backup-code-input"
              />
            </label>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={busy}
              data-testid="backup-code-submit"
            >
              {busy ? 'Verifying…' : 'Verify backup code'}
            </button>
            <button
              type="button"
              className="btn btn-link"
              onClick={backToTotp}
              disabled={busy}
              data-testid="backup-code-back"
            >
              Use your authenticator app instead
            </button>
          </form>
        )}

        {mode.kind === 'backup-codes' && (
          <BackupCodes onAcknowledged={() => void finishAuth()} />
        )}

        {mode.kind === 'passkey-nudge' && (
          <div data-testid="passkey-nudge">
            <h2>Add a passkey</h2>
            <p className="login-helper">
              Passkeys (Face ID, Touch ID, Windows Hello, or a security key) are
              faster than a password and protect your account from phishing.
              Add one now to sign in without a password next time. You can also
              do this later from Settings, Security.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAddPasskey}
              disabled={busy}
              data-testid="passkey-nudge-add"
            >
              {busy ? 'Waiting for your device…' : 'Add a passkey'}
            </button>
            <button
              type="button"
              className="btn btn-link"
              onClick={() => onAuthenticated()}
              disabled={busy}
              data-testid="passkey-nudge-skip"
            >
              Skip for now
            </button>
          </div>
        )}

        {mode.kind === 'forgot' && (
          <form onSubmit={handleForgotPassword} data-testid="forgot-form">
            <p className="login-helper">
              Enter your account email. We will send a verification code so you
              can choose a new password.
            </p>
            <label className="form-group">
              <span>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="forgot-email"
              />
            </label>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={busy}
              data-testid="forgot-submit"
            >
              {busy ? 'Sending…' : 'Send code'}
            </button>
            <button
              type="button"
              className="btn btn-link"
              onClick={() => switchTab('signin')}
              disabled={busy}
              data-testid="forgot-back"
            >
              Back to sign in
            </button>
          </form>
        )}

        {mode.kind === 'forgot-confirm' && (
          <form onSubmit={handleConfirmForgot} data-testid="forgot-confirm-form">
            <p className="login-helper">
              Code sent to <strong>{mode.email}</strong>.
            </p>
            <label className="form-group">
              <span>Verification code</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoComplete="one-time-code"
                data-testid="forgot-confirm-code"
              />
            </label>
            <label className="form-group">
              <span>New password</span>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={12}
                data-testid="forgot-confirm-password"
              />
            </label>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={busy}
              data-testid="forgot-confirm-submit"
            >
              {busy ? 'Resetting…' : 'Reset password'}
            </button>
            <button
              type="button"
              className="btn btn-link"
              onClick={() => switchTab('signin')}
              disabled={busy}
              data-testid="forgot-confirm-back"
            >
              Back to sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
